import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Bus, ChevronDown, ShieldCheck, Zap } from "lucide-react";
import { z } from "zod";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/Select";
import { Time12hPicker } from "@/shared/ui";
import { useCreatePosting, useUpdateShift } from "@/features/shifts/model/useShifts";
import {
  CATEGORY_LABELS,
  REQUIRED_CERTIFICATION_OPTIONS,
  ROLE_GROUPS,
  ROLE_LABELS,
  SHIFT_CATEGORIES,
  SHIFT_ROLES,
  type Shift,
  type ShiftCategory,
  type ShiftRole,
} from "@/features/shifts/types";
import { cn } from "@/shared/lib/cn";

// Default urgent premium % suggested when the venue toggles "urgent" without
// specifying a value. Mirrors core/config.py:urgent_premium_percent_default —
// server is authoritative if a venue posts without a value.
const DEFAULT_URGENT_PREMIUM_PERCENT = 50;
const MIN_URGENT_PREMIUM_PERCENT = 0;
const MAX_URGENT_PREMIUM_PERCENT = 200;

// Mirrors features/shifts/infrastructure/shifts_service.py:MIN_SHIFT_HOURS.
const MIN_SHIFT_HOURS = 3;
// Ontario general minimum wage. Updated annually on Oct 1 — keep in sync.
const MIN_PAY_RATE_CAD = 17.6;

const schema = z
  .object({
    // Multi-select on create — one posting gets created per role on submit.
    // Edit mode forces a single role via UI behavior; the schema still accepts
    // an array and the submit handler takes the first element.
    roles: z
      .array(z.enum(SHIFT_ROLES as [ShiftRole, ...ShiftRole[]]))
      .min(1, "Pick at least one role"),
    event_name: z.string().max(80, "Keep it under 80 characters").optional(),
    category: z.enum(SHIFT_CATEGORIES as [ShiftCategory, ...ShiftCategory[]]).optional(),
    date: z.string().min(1, "Date is required"),
    end_date: z.string().optional(),
    start_time: z.string().min(1, "Start time is required"),
    end_time: z.string().min(1, "End time is required"),
    pay_rate: z
      .string()
      .regex(/^\d+(\.\d{1,2})?$/, "Enter a valid amount like 25.00")
      .refine(
        (v) => parseFloat(v) >= MIN_PAY_RATE_CAD,
        `Pay rate must be at least $${MIN_PAY_RATE_CAD.toFixed(2)}/hr (Ontario minimum wage)`,
      ),
    // One worker count per selected role — 2 Bartenders + 1 Chef, not one
    // count silently multiplied across every role. Keys are roles; entries
    // for deselected roles linger in form state and are ignored.
    slots_by_role: z.record(z.string(), z.string()).default({}),
    is_urgent: z.boolean().optional().default(false),
    urgency_premium_percent: z
      .string()
      .regex(/^\d{1,3}$/, "Whole number 0-200")
      .refine(
        (v) =>
          parseInt(v) >= MIN_URGENT_PREMIUM_PERCENT &&
          parseInt(v) <= MAX_URGENT_PREMIUM_PERCENT,
        `Pick a value between ${MIN_URGENT_PREMIUM_PERCENT} and ${MAX_URGENT_PREMIUM_PERCENT}`,
      )
      .optional(),
    transportation_offered: z.boolean().optional().default(false),
    transportation_notes: z.string().max(500).optional(),
    required_certifications: z.array(z.string()).default([]),
    dress_code: z.string().max(255).optional(),
    notes: z.string().max(1000).optional(),
  })
  .refine(
    (v) => !v.end_date || v.end_date >= v.date,
    { path: ["end_date"], message: "End date must be on or after start date" },
  )
  .refine(
    (v) => {
      const h = computeHours(v.start_time, v.end_time);
      return h !== null && h >= MIN_SHIFT_HOURS;
    },
    {
      path: ["end_time"],
      message: `Shift must be at least ${MIN_SHIFT_HOURS} hours (Ontario ESA)`,
    },
  )
  .superRefine((v, ctx) => {
    // Only the selected roles' counts matter; a missing entry defaults to 1.
    for (const role of v.roles) {
      const raw = v.slots_by_role?.[role];
      if (raw === undefined || raw === "") continue;
      if (!/^\d+$/.test(raw) || parseInt(raw) < 1 || parseInt(raw) > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["slots_by_role", role],
          message: "1 to 50",
        });
      }
    }
  });

type FormValues = z.infer<typeof schema>;

function computeHours(start: string, end: string): number | null {
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let minutes = eh * 60 + em - (sh * 60 + sm);
  if (minutes <= 0) minutes += 24 * 60; // overnight
  return Math.round((minutes / 60) * 100) / 100;
}

interface Props {
  /** If provided, form is in edit mode for this shift. */
  existing?: Shift;
}

export function PostShiftForm({ existing }: Props) {
  const navigate = useNavigate();
  const create = useCreatePosting();
  const update = useUpdateShift(existing?.id ?? "");

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    control,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: existing
      ? {
          roles: [existing.role],
          event_name: existing.event_name ?? "",
          category: existing.category ?? undefined,
          date: existing.date,
          end_date: existing.end_date ?? "",
          start_time: existing.start_time.slice(0, 5),
          end_time: existing.end_time.slice(0, 5),
          pay_rate: existing.pay_rate,
          slots_by_role: {},
          is_urgent: existing.is_urgent ?? false,
          urgency_premium_percent:
            existing.urgency_premium_percent != null
              ? String(existing.urgency_premium_percent)
              : String(DEFAULT_URGENT_PREMIUM_PERCENT),
          transportation_offered: existing.transportation_offered ?? false,
          transportation_notes: existing.transportation_notes ?? "",
          required_certifications: existing.required_certifications ?? [],
          dress_code: existing.dress_code ?? "",
          notes: existing.notes ?? "",
        }
      : {
          roles: [],
          event_name: "",
          category: undefined,
          date: "",
          end_date: "",
          start_time: "18:00",
          end_time: "21:00",
          pay_rate: "",
          slots_by_role: {},
          is_urgent: false,
          urgency_premium_percent: String(DEFAULT_URGENT_PREMIUM_PERCENT),
          transportation_offered: false,
          transportation_notes: "",
          required_certifications: [],
          dress_code: "",
          notes: "",
        },
  });

  useEffect(() => {
    if (existing) {
      reset({
        roles: [existing.role],
        event_name: existing.event_name ?? "",
        category: existing.category ?? undefined,
        date: existing.date,
        end_date: existing.end_date ?? "",
        start_time: existing.start_time.slice(0, 5),
        end_time: existing.end_time.slice(0, 5),
        pay_rate: existing.pay_rate,
        slots_by_role: {},
        is_urgent: existing.is_urgent ?? false,
        urgency_premium_percent:
          existing.urgency_premium_percent != null
            ? String(existing.urgency_premium_percent)
            : String(DEFAULT_URGENT_PREMIUM_PERCENT),
        transportation_offered: existing.transportation_offered ?? false,
        transportation_notes: existing.transportation_notes ?? "",
        required_certifications: existing.required_certifications ?? [],
        dress_code: existing.dress_code ?? "",
        notes: existing.notes ?? "",
      });
    }
  }, [existing?.id, reset, existing]);

  const startTime = watch("start_time");
  const endTime = watch("end_time");
  const payRate = watch("pay_rate");
  const selectedRoles = watch("roles") ?? [];
  const isUrgent = watch("is_urgent");
  const urgencyPercentRaw = watch("urgency_premium_percent");
  const hours = useMemo(() => computeHours(startTime, endTime), [startTime, endTime]);

  // On create, pay_rate is the BASE — server multiplies by (1 + premium/100)
  // when is_urgent. On edit, pay_rate is the stored final rate, and changing
  // is_urgent / premium % is metadata only — the venue adjusts pay_rate
  // manually if they want a different worker-facing rate.
  const handleUrgentToggle = (next: boolean) => {
    setValue("is_urgent", next, { shouldDirty: true });
  };
  const urgencyPercentNum = useMemo(() => {
    if (!urgencyPercentRaw) return null;
    const n = parseInt(urgencyPercentRaw, 10);
    if (isNaN(n)) return null;
    return Math.max(
      MIN_URGENT_PREMIUM_PERCENT,
      Math.min(MAX_URGENT_PREMIUM_PERCENT, n),
    );
  }, [urgencyPercentRaw]);
  const baseRateNum = useMemo(() => {
    const n = parseFloat(payRate);
    return isNaN(n) ? null : n;
  }, [payRate]);
  const finalRateNum = useMemo(() => {
    if (baseRateNum == null) return null;
    if (isUrgent && urgencyPercentNum != null) {
      return baseRateNum * (1 + urgencyPercentNum / 100);
    }
    return baseRateNum;
  }, [baseRateNum, isUrgent, urgencyPercentNum]);
  const estimatedPay = useMemo(() => {
    if (!hours || finalRateNum == null) return null;
    return (hours * finalRateNum).toFixed(2);
  }, [hours, finalRateNum]);

  const durationTooShort = hours !== null && hours < MIN_SHIFT_HOURS;
  const payRateTooLow = baseRateNum !== null && baseRateNum < MIN_PAY_RATE_CAD;
  const hasIssue = durationTooShort || payRateTooLow;
  const issueReason = durationTooShort
    ? "Shift is shorter than the 3-hour minimum"
    : payRateTooLow
      ? `Pay rate is below Ontario minimum wage ($${MIN_PAY_RATE_CAD.toFixed(2)}/hr)`
      : null;

  const onSubmit = handleSubmit(async (values) => {
    const baseShared = {
      date: values.date,
      end_date: values.end_date && values.end_date !== values.date ? values.end_date : null,
      start_time: values.start_time,
      end_time: values.end_time,
      pay_rate: values.pay_rate,
      dress_code: values.dress_code || undefined,
      notes: values.notes || undefined,
    };

    const trimmedEventName = (values.event_name ?? "").trim();

    if (existing) {
      // Edit always touches a single shift's role — the picker enforces
      // single-select, but defensively take the first role anyway.
      await update.mutateAsync({
        ...baseShared,
        role: values.roles[0],
        category: values.category ?? null,
        event_name: trimmedEventName || null,
        is_urgent: values.is_urgent ?? false,
        urgency_premium_percent:
          values.is_urgent && values.urgency_premium_percent
            ? parseInt(values.urgency_premium_percent, 10)
            : null,
        transportation_offered: values.transportation_offered ?? false,
        transportation_notes:
          values.transportation_offered && values.transportation_notes
            ? values.transportation_notes
            : null,
        required_certifications: values.required_certifications ?? [],
      });
    } else {
      // One posting per selected role. Sequential awaits so a mid-loop
      // failure stops the rest and the venue sees a clear error rather than
      // a partial-success they have to reconcile.
      for (const role of values.roles) {
        await create.mutateAsync({
          ...baseShared,
          role,
          slots_requested: parseInt(values.slots_by_role?.[role] || "1"),
          is_urgent: values.is_urgent ?? false,
          urgency_premium_percent:
            values.is_urgent && values.urgency_premium_percent
              ? parseInt(values.urgency_premium_percent, 10)
              : undefined,
          category: values.category,
          event_name: trimmedEventName || undefined,
          transportation_offered: values.transportation_offered ?? false,
          transportation_notes:
            values.transportation_offered && values.transportation_notes
              ? values.transportation_notes
              : undefined,
          required_certifications: values.required_certifications ?? [],
        });
      }
      navigate("/venue");
    }
  });

  const mutation = existing ? update : create;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-1">
        <label className="text-sm font-medium">
          {existing ? "Role" : "Roles needed"}
        </label>
        <Controller
          name="roles"
          control={control}
          render={({ field }) => (
            <RoleGroupPicker
              selected={field.value ?? []}
              onChange={field.onChange}
              singleSelect={!!existing}
            />
          )}
        />
        {errors.roles && (
          <p className="text-xs text-destructive">{errors.roles.message as string}</p>
        )}
        {!existing && (
          <p className="text-xs text-muted-foreground">
            Pick one or more roles. We'll create a separate posting for each.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="event_name" className="text-sm font-medium">
          Event name <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <input
          id="event_name"
          type="text"
          maxLength={80}
          placeholder="e.g. Saturday Brunch, Summer Wine Tasting"
          {...register("event_name")}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {errors.event_name ? (
          <p className="text-xs text-destructive">{errors.event_name.message}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Workers see this as the shift title before your venue name. Leave blank to show role + venue.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="category" className="text-sm font-medium">
          Category <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value ?? ""}
              onValueChange={(v) => field.onChange(v || undefined)}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Pick a category" />
              </SelectTrigger>
              <SelectContent>
                {SHIFT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {CATEGORY_LABELS[cat]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <p className="text-xs text-muted-foreground">
          Helps workers filter by event type.
        </p>
      </div>

      <div className="grid grid-cols-1 items-start gap-x-4 gap-y-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="date" className="text-sm font-medium">
            Start date
          </label>
          <input
            id="date"
            type="date"
            {...register("date")}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.date && <p className="text-xs text-destructive">{errors.date.message}</p>}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="end_date" className="text-sm font-medium">
            End date <span className="font-normal text-muted-foreground">(optional, for multi-day)</span>
          </label>
          <input
            id="end_date"
            type="date"
            {...register("end_date")}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.end_date ? (
            <p className="text-xs text-destructive">{errors.end_date.message}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Leave blank for a single day. Same hours apply to each day.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-x-4 gap-y-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="start_time" className="text-sm font-medium">
            Start time
          </label>
          <Controller
            name="start_time"
            control={control}
            render={({ field }) => (
              <Time12hPicker
                id="start_time"
                value={field.value ?? ""}
                onChange={(v) => field.onChange(v)}
                step={15}
                ariaLabel="Start time"
                className="flex h-10 w-full"
              />
            )}
          />
          {errors.start_time && (
            <p className="text-xs text-destructive">{errors.start_time.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="end_time" className="text-sm font-medium">
            End time
          </label>
          <Controller
            name="end_time"
            control={control}
            render={({ field }) => (
              <Time12hPicker
                id="end_time"
                value={field.value ?? ""}
                onChange={(v) => field.onChange(v)}
                step={15}
                ariaLabel="End time"
                className="flex h-10 w-full"
              />
            )}
          />
          {errors.end_time && (
            <p className="text-xs text-destructive-foreground">
              {errors.end_time.message}
            </p>
          )}
        </div>
      </div>

      {hours !== null && (
        <div className="-mt-1 flex justify-end">
          <span
            className={cn(
              "text-xs tabular-nums",
              durationTooShort
                ? "font-medium text-destructive-foreground"
                : "text-muted-foreground",
            )}
          >
            Duration: {hours} hr{hours === 1 ? "" : "s"}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-x-4 gap-y-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="pay_rate" className="text-sm font-medium">
            Pay rate ($/hour){" "}
            {!existing && (
              <span className="font-normal text-muted-foreground">(base rate)</span>
            )}
          </label>
          <input
            id="pay_rate"
            type="text"
            inputMode="decimal"
            placeholder="25.00"
            {...register("pay_rate")}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.pay_rate && <p className="text-xs text-destructive">{errors.pay_rate.message}</p>}
          {!existing &&
            isUrgent &&
            finalRateNum != null &&
            baseRateNum != null &&
            urgencyPercentNum != null && (
              <p className="text-xs text-destructive-foreground">
                Workers see <strong>${finalRateNum.toFixed(2)}/hr</strong> (your $
                {baseRateNum.toFixed(2)} base + {urgencyPercentNum}% urgency
                premium)
              </p>
            )}
        </div>

        {!existing && (
          <div className="space-y-1.5">
            <label
              htmlFor={selectedRoles.length > 0 ? `slots_${selectedRoles[0]}` : undefined}
              className="text-sm font-medium"
            >
              {selectedRoles.length > 1 ? "Workers per role" : "Number of positions"}
            </label>
            {selectedRoles.length === 0 ? (
              <p className="flex h-10 items-center text-xs text-muted-foreground">
                Pick at least one role above first.
              </p>
            ) : (
              <div className="space-y-2">
                {selectedRoles.map((role) => (
                  <div key={role} className="flex items-center gap-2">
                    {selectedRoles.length > 1 && (
                      <span className="w-36 min-w-0 shrink-0 truncate text-sm text-copy">
                        {ROLE_LABELS[role]}
                      </span>
                    )}
                    <input
                      id={`slots_${role}`}
                      type="number"
                      min="1"
                      max="50"
                      defaultValue="1"
                      aria-label={`Number of ${ROLE_LABELS[role]} positions`}
                      {...register(`slots_by_role.${role}`, {
                        // Validate as the user types — waiting for submit to
                        // flag "0" on a count field reads as broken.
                        onChange: () => void trigger(`slots_by_role.${role}`),
                      })}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {errors.slots_by_role?.[role] && (
                      <p className="shrink-0 text-xs text-destructive">
                        {errors.slots_by_role[role]?.message as string}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!errors.slots_by_role && (
              <p className="text-xs text-muted-foreground">
                {selectedRoles.length > 1
                  ? "How many people you need for each role."
                  : "How many people you need for this role/time."}
              </p>
            )}
          </div>
        )}
      </div>

      <div
        className={cn(
          "rounded-md border px-4 py-3 transition-colors",
          hasIssue
            ? "border-destructive bg-destructive-light"
            : "border-border bg-surface-sunken",
        )}
      >
        <div className="flex items-baseline justify-between gap-3">
          <p
            className={cn(
              "text-xs font-semibold uppercase tracking-wider",
              hasIssue ? "text-destructive-foreground" : "text-muted-foreground",
            )}
          >
            Estimated total per person
          </p>
          {!hasIssue && (
            <span className="text-xs text-muted-foreground">
              Min {MIN_SHIFT_HOURS} hrs · ${MIN_PAY_RATE_CAD.toFixed(2)}/hr min wage
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span
            className={cn(
              "text-2xl font-semibold tabular-nums",
              hasIssue ? "text-destructive-foreground" : "text-copy",
            )}
          >
            {estimatedPay ? `$${estimatedPay}` : "—"}
          </span>
          {hours != null && finalRateNum != null && (
            <span
              className={cn(
                "text-xs tabular-nums",
                hasIssue ? "text-destructive-foreground" : "text-muted-foreground",
              )}
            >
              {hours} hr{hours === 1 ? "" : "s"} × ${finalRateNum.toFixed(2)}/hr
            </span>
          )}
        </div>
        {durationTooShort && (
          <p className="mt-2 text-xs text-destructive-foreground">
            Shift is {hours} hr{hours === 1 ? "" : "s"} — Ontario ESA requires at
            least {MIN_SHIFT_HOURS} hrs. Move the end time later.
          </p>
        )}
        {payRateTooLow && (
          <p className="mt-2 text-xs text-destructive-foreground">
            ${baseRateNum?.toFixed(2)}/hr is below Ontario minimum wage. Bump pay
            rate to at least ${MIN_PAY_RATE_CAD.toFixed(2)}/hr.
          </p>
        )}
      </div>

      <div className="space-y-1">
        <label htmlFor="dress_code" className="text-sm font-medium">
          Dress code <span className="text-muted-foreground">(optional)</span>
        </label>
        <input
          id="dress_code"
          {...register("dress_code")}
          placeholder="Black shirt and pants, non-slip shoes"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="notes" className="text-sm font-medium">
          Notes <span className="text-muted-foreground">(optional)</span>
        </label>
        <textarea
          id="notes"
          rows={3}
          {...register("notes")}
          placeholder="Anything workers should know — busy service, entry instructions, etc."
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="border-t border-border pt-4">
        <h3 className="text-sm font-semibold text-copy">Add-ons</h3>
        <p className="text-xs text-muted-foreground">
          All optional — skip what doesn't apply.
        </p>
      </div>

      <div
        className={
          "rounded-md border p-3 transition-colors " +
          (isUrgent
            ? "border-destructive bg-destructive-light"
            : "border-input bg-background hover:bg-accent")
        }
      >
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={isUrgent ?? false}
            onChange={(e) => handleUrgentToggle(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Zap className="h-4 w-4 text-destructive" />
              Mark as urgent
            </span>
            <span className="text-xs text-muted-foreground">
              {existing
                ? "Workers see an URGENT badge. The pay rate above is what they see — adjust it manually if you change the premium."
                : "Last-minute fill needed. Workers see an URGENT badge and the pay rate gets boosted by the percentage you choose below."}
            </span>
          </div>
        </label>
        {isUrgent && (
          <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-destructive pt-3">
            <div className="space-y-1">
              <label
                htmlFor="urgency_premium_percent"
                className="text-xs font-medium"
              >
                Urgency premium (%)
              </label>
              <input
                id="urgency_premium_percent"
                type="number"
                min={MIN_URGENT_PREMIUM_PERCENT}
                max={MAX_URGENT_PREMIUM_PERCENT}
                step="1"
                inputMode="numeric"
                {...register("urgency_premium_percent")}
                className="w-24 rounded-md border border-input bg-background px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {!existing && (
              <p className="flex-1 text-xs text-muted-foreground">
                Default {DEFAULT_URGENT_PREMIUM_PERCENT}%. Workers see the boosted
                rate{baseRateNum != null && finalRateNum != null && (
                  <>
                    : <strong>${finalRateNum.toFixed(2)}/hr</strong> (base $
                    {baseRateNum.toFixed(2)} × {urgencyPercentNum ?? 0}%)
                  </>
                )}.
              </p>
            )}
          </div>
        )}
        {errors.urgency_premium_percent && (
          <p className="mt-2 text-xs text-destructive">
            {errors.urgency_premium_percent.message}
          </p>
        )}
      </div>

      <div className="rounded-md border border-input bg-background p-3">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            {...register("transportation_offered")}
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Bus className="h-4 w-4" />
              Offer transportation
            </span>
            <span className="text-xs text-muted-foreground">
              Useful for late-night or remote venues. Workers see a "transit included" tag.
            </span>
          </div>
        </label>
        {watch("transportation_offered") && (
          <div className="mt-3 space-y-1">
            <label htmlFor="transportation_notes" className="text-xs font-medium">
              Transit details <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              id="transportation_notes"
              {...register("transportation_notes")}
              placeholder="Free shuttle from Hurdman Station 5:30pm; Uber back covered."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )}
      </div>

      <div className="rounded-md border border-input bg-background p-3">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <ShieldCheck className="h-4 w-4" />
          Required certifications{" "}
          <span className="text-muted-foreground">(optional)</span>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Workers without all selected certs will see a "missing certs" hint.
        </p>
        <Controller
          name="required_certifications"
          control={control}
          render={({ field }) => {
            const selected = field.value ?? [];
            const toggle = (cert: string, checked: boolean) => {
              if (checked) {
                if (!selected.includes(cert)) {
                  field.onChange([...selected, cert]);
                }
              } else {
                field.onChange(selected.filter((c) => c !== cert));
              }
            };
            return (
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {REQUIRED_CERTIFICATION_OPTIONS.map((cert) => {
                  const checked = selected.includes(cert);
                  return (
                    <label
                      key={cert}
                      className={
                        "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors " +
                        (checked
                          ? "border-primary-light bg-primary-light text-primary"
                          : "border-input bg-background hover:bg-surface-sunken")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggle(cert, e.target.checked)}
                        className="h-4 w-4 shrink-0"
                      />
                      <span className="min-w-0 truncate">{cert}</span>
                    </label>
                  );
                })}
              </div>
            );
          }}
        />
      </div>

      {mutation.error && (
        <p className="text-sm text-destructive">
          {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            "Could not save shift"}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={isSubmitting || (existing && !isDirty) || hasIssue}
          title={hasIssue ? (issueReason ?? undefined) : undefined}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending
            ? "Saving..."
            : existing
              ? "Save changes"
              : (watch("roles")?.length ?? 0) > 1
                ? `Post ${watch("roles")!.length} shifts`
                : "Post shift"}
        </button>
        {hasIssue && issueReason && (
          <p className="text-xs text-destructive-foreground">{issueReason}</p>
        )}
      </div>
    </form>
  );
}

interface RoleGroupPickerProps {
  selected: ShiftRole[];
  onChange: (next: ShiftRole[]) => void;
  /** Edit mode: only one role at a time (radio behavior). */
  singleSelect: boolean;
}

function RoleGroupPicker({ selected, onChange, singleSelect }: RoleGroupPickerProps) {
  // Auto-expand groups that already have a selected role so an edit
  // shows the current role without the user clicking through.
  const initialExpanded = useMemo(() => {
    const map: Record<string, boolean> = {};
    ROLE_GROUPS.forEach((g) => {
      if (g.roles.some((r) => selected.includes(r))) {
        map[g.label] = true;
      }
    });
    return map;
  }, []);
  const [expanded, setExpanded] = useState<Record<string, boolean>>(initialExpanded);

  const toggleGroup = (label: string) => {
    setExpanded((m) => ({ ...m, [label]: !m[label] }));
  };

  const toggleRole = (role: ShiftRole) => {
    if (singleSelect) {
      onChange([role]);
      return;
    }
    onChange(
      selected.includes(role)
        ? selected.filter((r) => r !== role)
        : [...selected, role],
    );
  };

  return (
    <div className="space-y-2">
      {!singleSelect && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => toggleRole(r)}
              className="inline-flex items-center gap-1 rounded-full border border-primary-light bg-primary-light px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary-light/80"
              title={`Remove ${ROLE_LABELS[r]}`}
            >
              {ROLE_LABELS[r]} <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}

      <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-surface">
        {ROLE_GROUPS.map((group) => {
          const isOpen = expanded[group.label] ?? false;
          const selectedInGroup = group.roles.filter((r) => selected.includes(r));
          return (
            <div key={group.label}>
              <button
                type="button"
                onClick={() => toggleGroup(group.label)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between bg-surface-sunken px-4 py-2.5 text-left transition-colors hover:bg-surface-sunken/80"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-copy-muted">
                  {group.label}
                </span>
                <div className="flex items-center gap-2">
                  {selectedInGroup.length > 0 && (
                    <span className="text-xs font-medium text-primary">
                      {selectedInGroup.length} selected
                    </span>
                  )}
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 text-copy-muted transition-transform",
                      isOpen && "rotate-180",
                    )}
                  />
                </div>
              </button>
              {isOpen && (
                <div className="grid grid-cols-1 gap-1 p-3 sm:grid-cols-2">
                  {group.roles.map((role) => {
                    const checked = selected.includes(role);
                    return (
                      <label
                        key={role}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-sunken"
                      >
                        <input
                          type={singleSelect ? "radio" : "checkbox"}
                          name="role-picker"
                          checked={checked}
                          onChange={() => toggleRole(role)}
                          className="h-4 w-4 shrink-0"
                        />
                        <span className="text-sm text-copy">{ROLE_LABELS[role]}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
